<template>
  <div class="chart-and-legend-container">
    <div class="chart-container" :style="{'--size': size + 'px'}">
      <Doughnut :data="data" :options="state.options" />
      <div id="chartjs-tooltip">
        <table></table>
      </div>
      <div v-if="total === 0" class="chart-shape" />
      <div v-if="displayTotal" class="total">
        <div class="number" v-text="totalDisplay" />
        <div class="total-label" v-text="totalLabel" />
      </div>
    </div>
    <div class="customLegend">
      <div class="label" v-for="(label, index) in data.labels" :key="index">
        <div class="inline">
          <div
            class="color"
            :style="{
              backgroundColor: data.datasets[0].backgroundColor[index],
            }"
          />
          <div class="pourcent">
            {{
              total
                ? Math.round((data.datasets[0].data[index] / total) * 100)
                : 0
            }}%
          </div>
        </div>
        {{ label }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .chart-and-legend-container {
    width: 100%;
    display: flex;
    gap: 28px;
    justify-content: space-around;
    align-items: center;

    .chart-container {
      flex: unset;

      canvas {
        width: var(--size);
        height: var(--size);
      }
    }

    .customLegend {
      display: flex;
      flex-direction: column;
      justify-content: start;
      margin: 0;

      .label {
        align-items: start;
      }
    }
  }
</style>

<script setup>
import { computed } from "vue";
import { Doughnut } from "vue-chartjs";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useDark } from "@vueuse/core";

const isDark = useDark();

ChartJS.register(ArcElement, Tooltip, Legend);

const total = computed(() => {
  return props.data.datasets[0].data.reduce(
    (accumulator, currentValue) => accumulator + currentValue
  );
});

const totalDisplay = computed(() => {
  return total.value > 999
    ? `${Math.floor(total.value / 1000)}K+`
    : total.value;
});

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  displayTotal: {
    type: Boolean,
    default: true,
  },
  totalLabel: {
    type: String,
  },
  cutout: {
    type: Number,
    default: 50,
  },
  cutoutPercent: {
    type: String,
    required: false,
  },
  radius: {
    type: String,
    required: false,
  },
  size: {
    type: Number,
    default: 250,
  }
});

const state = computed(() => {
  const borderColor = isDark.value ? "#1D1D1D" : "#FFFFFF";

  return {
    options: {
      responsive: false,
      maintainAspectRatio: false,
      cutout: props.cutoutPercent || props.cutout,
      radius: props.radius || "100%",
      elements: {
        arc: {
          borderWidth: 12,
          borderJoinStyle: "round",
          borderRadius: 10,
          borderColor: borderColor,
          hoverBorderColor: borderColor,
          hoverBorderWidth: 8,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: false
        },
        tooltip: {
          enabled: false,
          external: (context) => {
            let tooltipEl = document.getElementById("chartjs-tooltip");

            const tooltipModel = context.tooltip;

            if (tooltipModel.opacity === 0) {
              tooltipEl.classList.remove("show");
              return;
            }

            function getBody(bodyItem) {
              return bodyItem.lines;
            }

            if (tooltipModel.body) {
              const titleLines = tooltipModel.title || [];
              const bodyLines = tooltipModel.body.map(getBody);

              let innerHtml = "<thead>";

              titleLines.forEach(function (title) {
                innerHtml += "<tr><th>" + title + "</th></tr>";
              });
              innerHtml += "</thead><tbody>";

              bodyLines.forEach(function (body) {
                const span = "<span>" + body + "</span>";
                innerHtml += "<tr><td>" + span + "</td></tr>";
              });
              innerHtml += "</tbody>";

              let tableRoot = tooltipEl.querySelector("table");
              tableRoot.innerHTML = innerHtml;
            }

            tooltipEl.classList.add("show");

            if (window.innerWidth > 992) {
              tooltipEl.style.left = tooltipModel.caretX + "px";
              tooltipEl.style.top =
                tooltipModel.caretY - tooltipModel.height / 2 + "px";
            }
          },
        },
      },
    },
  };
});
</script>
